import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  MOTION_ARTWORK_LENGTHS,
  MotionArtworkLengthSec,
  runCredits,
  STUDIO_THEMES,
} from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../../common/types';
import { studioMessages } from '../studioMessages';
import { fullVideoMessages } from '../fullVideoMessages';
import { motionLength } from '../studioRules';
import { StudioDetailsProps } from './props';
import { DetailField } from './DetailField';
import { ChoiceChip, ChoiceChips } from './ChoiceChips';

/** The credits of the pair (square and tall): one charge for the run. */
const PAIR_CREDITS = runCredits('motionArtwork', STUDIO_THEMES.motionArtwork.outputs);

/** The chip value of "Whole bars, about 20 s" (no `lengthSec`: the server fits the bars). */
const WHOLE_BARS = 0;

/**
 * Step 3 of the motion artwork (designs s5, s7c, w): the length (whole bars of the song near 20 s
 * first; 15, 20 or 30 s override it), the two files and their price. The theme gives the motion;
 * there is no style choice (design w dropped Liquid) and no quality choice (one rate for every plan).
 */
const MotionArtworkDetailsComponent: React.FC<StudioDetailsProps> = ({ details, onChange }) => {
  const intl = useIntl();
  const title = intl.formatMessage(studioMessages.lengthTitle);
  const lengths = useMemo<Array<ChoiceChip<number>>>(
    () => [
      { value: WHOLE_BARS, label: intl.formatMessage(fullVideoMessages.motionWholeBars) },
      ...MOTION_ARTWORK_LENGTHS.map((value) => ({
        value,
        label: intl.formatMessage(studioMessages.seconds, { count: value }),
      })),
    ],
    [intl]
  );
  const lengthSec = motionLength(details.lengthSec) ?? WHOLE_BARS;

  return (
    <>
      <DetailField title={title} hint={intl.formatMessage(fullVideoMessages.motionLengthHint)}>
        <ChoiceChips
          label={title}
          options={lengths}
          value={lengthSec}
          onChange={(next) =>
            onChange({ lengthSec: next === WHOLE_BARS ? undefined : (next as MotionArtworkLengthSec) })
          }
        />
      </DetailField>
      <DetailField title={intl.formatMessage(fullVideoMessages.motionFilesTitle)}>
        <span>{intl.formatMessage(fullVideoMessages.motionFiles)}</span>
        <span className="small text-muted">
          {intl.formatMessage(fullVideoMessages.motionPrice, { count: PAIR_CREDITS })}
        </span>
        <span className="small text-muted">{intl.formatMessage(fullVideoMessages.motionRule)}</span>
      </DetailField>
    </>
  );
};

export const MotionArtworkDetails = TypedMemo(MotionArtworkDetailsComponent);
